import { combineLatest, map, takeUntil, filter } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'

import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'

import { Observable, Subscription } from 'rxjs'

import * as cart from '../../../cart/actions/cart'
import * as products from '../../actions/product'
import * as facet from '../../actions/facet'
import * as catalog from '../../actions/catalog'
import * as preferences from '../../../base/actions/preferences.actions'

import * as fromCatalog from '../../reducers'
import * as fromCart from '../../../cart/reducers'
import * as fromBase from '../../../base/reducers'
import * as fromAuth from '../../../auth/reducers'
import * as fromOrderHistory from '../../../../app/orderhistory/reducers'

import { FacetToggleEvent } from '../../../facets/models/facet-toggle-event'
import { FacetState } from '../../../facets/models/facet-state'
import { ProductState } from '../../models/product-state'
import { FilterToggleEvent } from '../../../shared/models/filter';

import { PageRoutingService } from '../../../core/services/page-routing.service'
import { FacetRoutingService } from '../../../facets/services/facet-routing-service'

import { ContainerBase } from '../../../core/base/container-base'
import { AddCartEvent, ApiOrderline, CartProduct } from '../../../cart/models/cart-models'
import { Collapsable } from '../../models/collapsable';
import { FavoriteProduct, ToggleFavoriteEvent } from '../../models/favorite-product';
import chaining from '../../../core/helpers/chaining';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DetailsContainer } from '../details-container/details-container';
import { MenuItems } from '../../../base/models/menu-items';
import { Setting } from '../../../core/models/setting';
import { CombinedFacets } from '../../../facets/models/facet';
import { Product } from '../../../core/models/product';
import { UXPage } from '../../../core/models/ux-page';
import { OrderHistory } from '../../../../app/orderhistory/models/orderhistory'
import { Crumb } from '../../../core/models/crumb';
import { SortOrder } from '../../models/sort-order';

@Component({
  selector: 'app-catalog-container',
  templateUrl: './template.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogContainer extends ContainerBase implements OnDestroy {
  loading = true
  loaded$: Observable<boolean>

  sideMenu$: Observable<Collapsable[]>

  cartProducts$: Observable<{[product: string]: CartProduct}>
  favoritesProducts$: Observable<FavoriteProduct[]>

  viewType$: Observable<string>
  viewTypeAdjustable$: Observable<boolean>
  productState$: Observable<ProductState>
  facetState$: Observable<FacetState>
  combinedFacets$: Observable<CombinedFacets>

  orderLines$: Observable<ApiOrderline[]>
  orderLines: ApiOrderline[]

  detailsPageSettings$: Observable<Setting>
  catalogPageSettings$: Observable<Setting>
  catalogItemPageSettings$: Observable<Setting>
  searchPageSettings$: Observable<Setting>
  quantityPicker$: Observable<string>
  catalogSortOrders$: Observable<SortOrder[]>

  currentPage$: Observable<UXPage>
  sideMenuIds$: Observable<string[]>
  menuItems$: Observable<MenuItems>
  activeChain$: Observable<string[]>
  showCrumbs: boolean;
  crumbs$: Observable<Crumb[]>;
  sortOrder$: Observable<string>

  facetRoutingSub: Subscription

  allowOutOfStockOrdering: boolean = false

  constructor(
    private modalSvc: NgbModal,
    private router: Router,
    private store: Store<fromCatalog.State>,
    private facetRouting: FacetRoutingService,
    private translate: TranslateService,
    private pageRouting: PageRoutingService,
    private route: ActivatedRoute
  ) {
    super()

    // Observables
    this.productState$ = store
      .pipe(
        select(fromCatalog.getProductState),
        takeUntil(this.destroyed$)
      )
    this.facetState$ = store
      .pipe(
        select(fromCatalog.getFacetState),
        takeUntil(this.destroyed$)
      )
    this.cartProducts$ = store
      .pipe(
        select(fromCart.getCartProducts),
        takeUntil(this.destroyed$)
      )
    this.favoritesProducts$ = store
      .pipe(
        select(fromCatalog.getFavorites),
        takeUntil(this.destroyed$)
      )
    this.viewType$ = store
      .pipe(
        select(fromBase.getViewType),
        takeUntil(this.destroyed$)
      )
    this.viewTypeAdjustable$ = store
      .pipe(
        select(fromBase.getViewTypeAdjustable),
        takeUntil(this.destroyed$)
      )
    this.sideMenu$ = store
      .pipe(
        select(fromBase.getSideMenuList),
        takeUntil(this.destroyed$)
      )
    this.orderLines$ = store
      .pipe(
        select(fromCart.getCurrentOrderLines),
        takeUntil(this.destroyed$)
      )
    this.combinedFacets$ = store
      .pipe(
        select(fromCatalog.getCombinedFacets),
        takeUntil(this.destroyed$)
      )
    this.currentPage$ = store
      .pipe(
        select(fromBase.getCurrentPage),
        takeUntil(this.destroyed$)
      )
    this.catalogSortOrders$ = store
      .pipe(
        select(fromBase.getCatalogSortOrders),
        takeUntil(this.destroyed$)
      )

    this.sortOrder$ = store
      .pipe(
        select(fromCatalog.getProductSortOrder),
        takeUntil(this.destroyed$)
      )

    this.detailsPageSettings$ = this.store.pipe(select(fromBase.getItemDetailsPageSettings), takeUntil(this.destroyed$))
    this.catalogItemPageSettings$ = this.store.pipe(select(fromBase.getCatalogItemPageSettings), takeUntil(this.destroyed$))
    this.catalogPageSettings$ = this.store.pipe(select(fromBase.getCatalogPageSettings), takeUntil(this.destroyed$))
    this.searchPageSettings$ = this.store.pipe(select(fromBase.getSearchPageSettings), takeUntil(this.destroyed$))
    this.store.pipe(
      select(fromAuth.getUserPermissions),
      takeUntil(this.destroyed$)
    ).subscribe((permissions) => {
      // First check if user is allowed to see stock info. If not, allowOutOfStockOrdering should be true since no stock info is available
      if (permissions.indexOf('VIEW_STOCK_INFO') === -1) {
        this.allowOutOfStockOrdering = true;
      } else {
        // User is allowed to see stock. Check for ALLOW_OUT_OF_STOCK_ORDERING permission.
        if (permissions.indexOf('ALLOW_OUT_OF_STOCK_ORDERING') > -1) {
          this.allowOutOfStockOrdering = true;
        }
      }
    })

    this.quantityPicker$ = this.store.pipe(
      select(fromBase.getQuantityPicker),
      takeUntil(this.destroyed$)
    )

    this.sideMenuIds$ = this.store.pipe(select(fromBase.getSideMenuIds), takeUntil(this.destroyed$))
    this.menuItems$ = this.store.pipe(select(fromBase.getMenuEntities), takeUntil(this.destroyed$))
    this.activeChain$ = this.store.pipe(select(fromBase.getActiveMenuChain), takeUntil(this.destroyed$))


    // get show breadcrumbs
    this.store.pipe(select(fromBase.getTenantSettings), takeUntil(this.destroyed$)).subscribe(settings => {
        this.showCrumbs = settings.show_breadcrumbs
      }
    )

    // get crumbs
    this.crumbs$ = this.pageRouting.breadCrumbs$

    // Dispatch init
    this.store.dispatch(new catalog.Init())

    // Subscriptions
    this.facetRoutingSub = facetRouting
      .pipe(
        map(facets => new facet.Apply(facets))
      ).subscribe(store)

    this.facetState$.pipe(
      combineLatest(
        this.productState$,
        (facets, products) => facets.loading || products.loading
      )).subscribe(loading => this.loading = loading)

    this.loaded$ = this.store.pipe(
      select(fromCatalog.getProductsLoaded),
      takeUntil(this.destroyed$)
    )

    this.orderLines$
      .subscribe(orderLines => this.orderLines = orderLines)
  }

  ngOnDestroy() {
    // Reset facet filter state & products state
    this.store.dispatch(new facet.FilterReset())
    this.store.dispatch(new products.ClearState())

    super.ngOnDestroy()
    this.facetRoutingSub.unsubscribe()
  }

  expandProducts() {
    this.store.dispatch(new products.Expand())
  }

  facetToggled(event: FacetToggleEvent) {
    this.facetRouting.toggleFacet(event)
  }

  groupToggled(event: string) {
    this.store.dispatch(new facet.ShowMore(event))
  }

  onlyInStockToggled() {
    this.store.dispatch(new products.ToggleInStock())
  }

  openDetails(product: Product) {
    this.store.dispatch(new products.Select(product))
    this.modalSvc.open(DetailsContainer, {centered: true, size: 'lg'})
  }

  toggleFavorite(event: ToggleFavoriteEvent) {
    event.isFavorite
      ? this.store.dispatch(new products.RemoveFavorite(event.product))
      : this.store.dispatch(new products.AddFavorite(event.product))
  }

  addCart(event: AddCartEvent) {
    const orderLineId = chaining(this.orderLines.find(line => chaining(line, 'product._id') === chaining(event, 'product')), '_id')

    if (chaining(event, 'quantity') > 0) {
      this.store.dispatch(new cart.Add({
        product: event.product,
        quantity: event.quantity
      }))
    } else if (event.quantity <=0 && orderLineId)
      this.store.dispatch(new cart.Remove({
        lineId: orderLineId
      }))
  }

  typeSwitch(type: string){
    this.store.dispatch(new preferences.SetViewType(type))
  }

  onFilterToggled(event: FilterToggleEvent) {
    this.store.dispatch(new products.SetProductFilter(event))
  }

  onSortOrderToggle(event: string) {
    this.store.dispatch(new products.SetProductSortOrder(event))
  }
}
